// DTOs del "perfil individual de persona" (coordinador → líder o supervisor).
//
// Vista de detalle de UNA persona, complementaria a C7 (/analisis-personal,
// que lista a TODOS). El endpoint detecta el rol del :userId y responde la
// sección correspondiente (lider | supervisor), más bloques comunes.
//
// Endpoint:
//   GET /api/coordinador/personal/:userId?desde&hasta
//
// REGLAS DE ORO (bonos sobre personas — la corrección de datos es el req #1):
//   - Producido: SOLO registros_produccion_hora.estado='valido'.
//   - Esperado: contra CAPACIDAD (skus_lineas.capacidad_estandar_unidades_hora),
//     NUNCA contra programa Alpina.
//   - Scope: la persona DEBE pertenecer a las plantas del coordinador → 403.
import { z } from 'zod';
// =============================================================================
// Query
// =============================================================================
export const coordinadorPersonalPerfilQuerySchema = z.object({
    desde: z.string().date().optional(),
    hasta: z.string().date().optional(),
});
// =============================================================================
// Persona
// =============================================================================
export const personalPerfilPersonaSchema = z.object({
    user_id: z.string().uuid(),
    nombre: z.string(),
    apellido: z.string(),
    email: z.string().email(),
    rol: z.enum(['lider', 'supervisor']),
    /** Área de la línea del líder. null para supervisor o líder sin línea. */
    area_nombre: z.string().nullable(),
    /** Nombre legible de la línea del líder (ej. "Vertical 1"). null para supervisor o líder sin línea. */
    linea_nombre: z.string().nullable(),
    /** Código de la línea del líder (ej. "V1"). null para supervisor o líder sin línea. */
    linea_codigo: z.string().nullable(),
    /** Plantas del supervisor (nombres). Vacío para líder. */
    plantas: z.array(z.string()),
});
// =============================================================================
// KPIs comunes
// =============================================================================
export const personalPerfilKpisSchema = z.object({
    /** Unidades producidas (SOLO estado='valido') en el rango. */
    producido: z.number().min(0),
    /** Capacidad esperada acumulada (skus_lineas) de las horas producidas. */
    esperado_capacidad: z.number().min(0),
    /** producido / esperado_capacidad * 100. null si esperado=0. */
    cumplimiento_meta_pct: z.number().min(0).nullable(),
    /** Bloques (= horas) contados. */
    horas: z.number().int().min(0),
    /** Días distintos con actividad. */
    dias: z.number().int().min(0),
});
// =============================================================================
// Comparativo vs período anterior simétrico
// =============================================================================
export const personalPerfilComparativoSchema = z.object({
    /** Delta % de producido vs período anterior. */
    delta_producido_pct: z.number().nullable(),
    /** Delta en PUNTOS PORCENTUALES del cumplimiento_meta_pct. */
    delta_cumplimiento_pp: z.number().nullable(),
    /** Delta % de horas. */
    delta_horas_pct: z.number().nullable(),
});
// =============================================================================
// Posición (ranking por cumplimiento_meta_pct)
// =============================================================================
export const personalPerfilPosicionSchema = z.object({
    rank: z.number().int().min(1),
    total: z.number().int().min(1),
    /** Ámbito del ranking (nombre del área para líder, 'supervisores' para sup). */
    ambito: z.string(),
});
// =============================================================================
// Serie por día
// =============================================================================
export const personalPerfilSerieDiaSchema = z.object({
    fecha: z.string().date(),
    producido: z.number().min(0),
    esperado: z.number().min(0),
    cumplimiento_meta_pct: z.number().min(0).nullable(),
    horas: z.number().int().min(0),
});
// =============================================================================
// Sección LÍDER
// =============================================================================
const liderPorSkuSchema = z.object({
    linea_codigo: z.string(),
    sku_codigo: z.string(),
    sku_descripcion: z.string(),
    producido: z.number().min(0),
    esperado: z.number().min(0),
    cumplimiento_meta_pct: z.number().min(0).nullable(),
    horas: z.number().int().min(0),
});
const liderCalidadSchema = z.object({
    /** COUNT(validado_at not null) / COUNT(*) * 100. null si no hay registros. */
    pct_validado: z.number().min(0).max(100).nullable(),
    /** COUNT(estado='invalidado') / COUNT(*) * 100. null si no hay registros. */
    pct_invalidado: z.number().min(0).max(100).nullable(),
    /**
     * Puntualidad: AVG en minutos del lag entre captura (created_at) y el inicio
     * del bloque (fecha + hora_inicio_bloque, coercionado en tz tenant). Positivo
     * = capturó después de empezar el bloque. null si no hay registros.
     */
    puntualidad_min: z.number().nullable(),
    /** COUNT(es_captura_fuera_turno=true). */
    fuera_turno: z.number().int().min(0),
});
const liderAnomaliasSchema = z.object({
    p1: z.number().int().min(0),
    p2: z.number().int().min(0),
    p3: z.number().int().min(0),
    p4: z.number().int().min(0),
    p5: z.number().int().min(0),
    p6: z.number().int().min(0),
});
const liderTrazabilidadSchema = z.object({
    registros_totales: z.number().int().min(0),
    registros_validados: z.number().int().min(0),
    /** Ediciones post-captura: eventos_auditoria update sobre sus registros. */
    ediciones_post_captura: z.number().int().min(0),
    /** Nombres distintos de quienes validaron sus registros. */
    validado_por: z.array(z.string()),
});
export const personalPerfilLiderSchema = z.object({
    /** GROUP BY (linea, sku) sobre sus registros válidos. */
    por_sku: z.array(liderPorSkuSchema),
    calidad: liderCalidadSchema,
    anomalias: liderAnomaliasSchema,
    trazabilidad: liderTrazabilidadSchema,
});
// =============================================================================
// Sección SUPERVISOR
// =============================================================================
const supervisorIntervencionSchema = z.object({
    validaciones: z.number().int().min(0),
    invalidaciones: z.number().int().min(0),
    paros_cerrados: z.number().int().min(0),
    capturas_delegadas: z.number().int().min(0),
});
const supervisorTurnoDetalleSchema = z.object({
    fecha: z.string().date(),
    turno_nombre: z.string(),
    planta_nombre: z.string(),
    producido: z.number().min(0),
    esperado: z.number().min(0),
    validaciones: z.number().int().min(0),
});
export const personalPerfilSupervisorSchema = z.object({
    /** AVG(validado_at - created_at) en minutos de lo que validó. null si no validó. */
    oportunidad_min: z.number().nullable(),
    /** % de registros de sus turnos que están validados. null si no hay registros. */
    pct_validado_turno: z.number().min(0).max(100).nullable(),
    intervencion: supervisorIntervencionSchema,
    /** Sus turnos cubiertos, ordenado por fecha DESC. */
    turnos_detalle: z.array(supervisorTurnoDetalleSchema),
});
// =============================================================================
// Response
// =============================================================================
export const coordinadorPersonalPerfilSchema = z.object({
    persona: personalPerfilPersonaSchema,
    periodo: z.object({ desde: z.string().date(), hasta: z.string().date() }),
    kpis: personalPerfilKpisSchema,
    comparativo: personalPerfilComparativoSchema.nullable(),
    posicion: personalPerfilPosicionSchema.nullable(),
    serie_dia: z.array(personalPerfilSerieDiaSchema),
    lider: personalPerfilLiderSchema.nullable(),
    supervisor: personalPerfilSupervisorSchema.nullable(),
});
//# sourceMappingURL=coordinador_personal_perfil.js.map